import { useQuery } from '@tanstack/react-query';

import type { GetAssignVinResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get assigned VIN data from a purchased vehicle
 */
export const getAssignVin = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetAssignVinResponse> => {
	if (!orderId) {
		throw new Error('Unable to get assigned VIN without an order ID.');
	}
	const response = await apiClient.get<GetAssignVinResponse>(
		ordersEndpoints.assignVin(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting assigned VIN data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetAssignVin = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.assignVin.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get assigned VIN without an order ID.');
			}
			return getAssignVin(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
